/**
 * What the UC2 map paints for the active chip (design §3).
 *
 * Pure, and in its own module rather than inside `Uc2.tsx`, for the two reasons
 * `uc1Paint.ts` is: the component file stays components-only (React Fast Refresh), and
 * "what colour is this watershed for this chip" is the one rule in the pane worth testing
 * on its own.
 *
 * Every chip is painted from the **units rows alone** (`cropland_delta_pct`,
 * `rain_tendency`, `adoption`, `category`), so opening UC2 is one request and clicking is
 * one more, exactly as the client's §0.2 budget says. (Client round 2 Oct 2026: the
 * `similar` chip, the only one that also needed the clicked watershed's own detail, is
 * gone — the alike watersheds are the Similar tab's subject and are read there.)
 *
 * A row from the pre-launch run carries none of the new fields; every chip then hatches it
 * as not assessed rather than throwing, which is the truth about such a row.
 */
import type { IntelUnitRow, Layer } from '../../lib/api';
import { BOUNDARY_COLOR } from '../map/resultUnits';
import {
  CROPLAND_BASE,
  POSITIVE_BASE,
  NEUTRAL_COLOR,
  PRACTICE_BASE,
  RAINFALL_BASE,
  SELECTED_OUTLINE_COLOR,
  STABILITY_BAND_PCT,
  UC2_CATEGORY_COLORS,
  UC2_GREENING_FALLBACK_CLASSES,
  UC2_LC_CHANGE_FALLBACK_CLASSES,
  familySteps,
  positiveStep,
  practiceStep,
  type Uc2Chip,
} from './uc2Constants';

/**
 * One legend row: a class the active chip actually painted, with how many units fell in it
 * — `n` absent on the land-cover chip, whose classes are the raster palette's own and
 * count no watersheds.
 */
export interface Uc2Class {
  label: string;
  color: string;
  hatched?: boolean;
  n?: number;
}

/** What one chip's pass over the units rows produces for the result layer and the legend. */
export interface Uc2Paint {
  colors: Record<string, string>;
  patterns: Record<string, 'hatched'>;
  details: Record<string, string>;
  /** The thick outline a unit is drawn with — the selected watershed, on every chip. */
  outlines: Record<string, string>;
  classes: Uc2Class[];
  /** Printed under the legend when the chip has no classes of its own (a raster chip). */
  note: string | null;
}

export interface Uc2PaintInput {
  rows: IntelUnitRow[];
  chip: Uc2Chip;
  /** `#uc2Practice` — which group the practices chip reads. */
  practice: string;
  /** The clicked watershed, outlined on every chip; `null` before the first click. */
  selected: string | null;
  /**
   * `GET /layers`' own row for `intel_uc2_ndvi_trend`, for the greening chip's legend —
   * the catalogue is the authority on how that COG is painted, and a palette spelled a
   * second time here would drift from the pixels the moment the layer is restyled.
   * `undefined` before `/layers` lands, and `null` for a catalogue that has no such row.
   */
  greeningLayer?: Layer | null;
  /** `/meta/config.ramps`, where a non-categorical layer's `ramp` key resolves its stops. */
  ramps?: Record<string, string[]>;
  /** The run's `summary.stability_band`, in percent; `STABILITY_BAND_PCT` when unserved. */
  band?: number;
  /**
   * `#intelLevel` — `'watershed'` (the default) or `'admin0'`. A country is a summary of
   * its watersheds (2 Oct chart round, design §2.1): the pattern chip paints the run's own
   * `share_positive_pct`, and the three evidence chips paint a share derived here from the
   * watershed rows — no new endpoint and no new field.
   */
  level?: string;
  /**
   * The watershed rows, for the country-level shares. The pane holds them whatever level
   * the switch is on (the same cached `uc2/units?level=watershed` it fetched on open), so
   * this costs no request. Empty means the shares cannot be computed and the countries
   * hatch rather than being painted a number nobody has.
   */
  watershedRows?: IntelUnitRow[];
  /**
   * `GET /layers`' own row for `intel_uc2_lc_change`, for the land-cover chip's legend —
   * read for the reason `greeningLayer` is: the catalogue is the authority on how that
   * COG is painted.
   */
  lcChangeLayer?: Layer | null;
  label: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * The greening chip's legend rows, read off the catalogue the way `widgets/Legend.tsx`'s
 * `LayerBlock` reads any switched-on raster: a categorical layer prints its own `legend`
 * entries, and a continuous one resolves `ramp` into `/meta/config.ramps` and is labelled
 * by its two ends. A catalogue with neither falls back to the two words themselves, so the
 * chip is never a colour with no meaning attached.
 */
export function greeningClasses(
  layer: Layer | null | undefined,
  ramps: Record<string, string[]> | undefined,
  label: Uc2PaintInput['label'],
): Uc2Class[] {
  const served = layer?.legend ?? [];
  if (layer?.is_categorical && served.length) {
    return served.map((entry) => ({ label: entry.label, color: entry.color }));
  }
  const stops = layer?.ramp ? (ramps?.[layer.ramp] ?? []) : [];
  if (stops.length >= 2) {
    // Greening first, browning second — the same order as the fallback key below, so the
    // row a reader learns is in the same place whichever of the two the catalogue allows.
    return [
      { label: label('intel.uc2.legend.greening.up'), color: stops[stops.length - 1]! },
      { label: label('intel.uc2.legend.greening.down'), color: stops[0]! },
    ];
  }
  return UC2_GREENING_FALLBACK_CLASSES.map((c) => ({
    label: label(`intel.uc2.legend.greening.${c.id}`),
    color: c.color,
  }));
}

/**
 * The land-cover change chip's legend: the published `intel_uc2_lc_change` layer's own
 * classes, exactly as the Layers pane reads any categorical raster. A catalogue that has
 * not landed yet (or an older server with no such row) falls back to the five class names
 * and colours the precompute writes, so the raster on screen is never a palette with
 * nothing said about it.
 */
export function lcChangeClasses(
  layer: Layer | null | undefined,
  label: Uc2PaintInput['label'],
): Uc2Class[] {
  const served = layer?.legend ?? [];
  if (served.length) return served.map((entry) => ({ label: entry.label, color: entry.color }));
  return UC2_LC_CHANGE_FALLBACK_CLASSES.map((c) => ({
    label: label(`intel.uc2.lcChangeClass.${c.id}`),
    color: c.color,
  }));
}

/**
 * The legend row for the transparent watershed layer a raster chip draws in place of its
 * choropleth (client round 2 Oct 2026). No `n`: like the raster palette rows it sits
 * beside, it describes a line on the map rather than a class the units fell into — and
 * nothing on screen should be unexplained, a hairline a reader can click least of all.
 */
export function boundariesClass(label: Uc2PaintInput['label']): Uc2Class {
  return { label: label('intel.uc2.legend.boundaries'), color: BOUNDARY_COLOR };
}

/**
 * Which of the three cropland steps a relative change falls in, or `null` with no value.
 * `band` is the run's own `stability_band` (`uc2/summary`); the default is for a summary
 * that carries none, so the map is never painted to a band the engine did not use.
 */
export function croplandStep(
  delta: number | null | undefined,
  band: number = STABILITY_BAND_PCT,
): 0 | 1 | 2 | null {
  if (typeof delta !== 'number' || !Number.isFinite(delta)) return null;
  if (delta < -band) return 0;
  if (delta > band) return 2;
  return 1;
}

/** `drier | stable | wetter` → its step, or `null` for a row with no tendency. */
export function rainStep(tendency: string | null | undefined): 0 | 1 | 2 | null {
  if (tendency === 'drier') return 0;
  if (tendency === 'stable') return 1;
  if (tendency === 'wetter') return 2;
  return null;
}

const CROPLAND_KEYS = ['declining', 'stable', 'growing'] as const;
const RAIN_KEYS = ['drier', 'stable', 'wetter'] as const;
const PRACTICE_KEYS = ['low', 'mid', 'high'] as const;
const POSITIVE_KEYS = ['low', 'mid', 'high'] as const;

/**
 * The watershed rows that belong to one country row. `country` on the row is the
 * authority when the run serves it; otherwise the id convention (`BFWS01` sits under
 * `BF`), which is how every watershed id in this programme is built.
 */
export function watershedsOf(rows: IntelUnitRow[] | undefined, countryId: string): IntelUnitRow[] {
  return (rows ?? []).filter((row) =>
    row.country ? row.country === countryId : row.unit_id.startsWith(countryId),
  );
}

/** The share of rows a test says yes to, in percent, over the rows it could answer for. */
function shareOf(rows: IntelUnitRow[], test: (row: IntelUnitRow) => boolean | null): number | null {
  let yes = 0;
  let answered = 0;
  for (const row of rows) {
    const verdict = test(row);
    if (verdict == null) continue;
    answered += 1;
    if (verdict) yes += 1;
  }
  return answered ? (yes / answered) * 100 : null;
}

/** The mean of the numbers among `values`, or `null` when there are none. */
export function meanOf(values: (number | null | undefined)[]): number | null {
  const numbers = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return numbers.length ? numbers.reduce((a, b) => a + b, 0) / numbers.length : null;
}

export function chipPaint({
  rows,
  chip,
  practice,
  selected,
  greeningLayer,
  lcChangeLayer,
  ramps,
  band = STABILITY_BAND_PCT,
  level = 'watershed',
  watershedRows,
  label,
}: Uc2PaintInput): Uc2Paint {
  const colors: Record<string, string> = {};
  const patterns: Record<string, 'hatched'> = {};
  const details: Record<string, string> = {};
  const outlines: Record<string, string> = {};
  const classes: Uc2Class[] = [];
  const note: string | null = null;

  const hatch = (id: string, reason: string | null | undefined) => {
    patterns[id] = 'hatched';
    details[id] = reason
      ? label('intel.uc2.legend.notAssessedReason', {
          reason: label(`intel.uc2.reason.${reason}`, { defaultValue: reason }),
        })
      : label('intel.uc2.legend.notAssessed');
  };

  /** One legend row, counted off what was actually painted; a class with no unit is dropped. */
  const add = (text: string, color: string, test: (id: string) => boolean, hatched = false) => {
    const n = rows.filter((r) => test(r.unit_id)).length;
    if (n) classes.push({ label: text, color, n, ...(hatched ? { hatched: true } : {}) });
  };

  /**
   * Land cover: the chip's evidence is the two rasters under the swipe handle, not a
   * choropleth, so nothing is coloured, hatched or outlined here — `Uc2.tsx` takes the
   * result layer off the map entirely, and a colour returned from this branch would be a
   * fill over the very thing the chip exists to show. The legend is the published palette
   * the two COGs are painted in, plus which year is on which side of the handle.
   */
  if (chip === 'landcover') {
    return {
      colors,
      patterns,
      details,
      outlines,
      classes: [...lcChangeClasses(lcChangeLayer, label), boundariesClass(label)],
      note: label('intel.uc2.legend.changeNote'),
    };
  }

  /**
   * Greening: the same shape as land cover, for the same reason — the chip's evidence is
   * the published `intel_uc2_ndvi_trend` COG, which `Uc2.tsx` switches on and the result
   * layer comes off for, so a choropleth painted here would be a fill over it. It used to
   * fall through to the `pattern` branch, which coloured every watershed by its land-cover
   * verdict: a reader on the greening chip was shown the pattern map and told it was the
   * satellite trend.
   */
  if (chip === 'greening') {
    return {
      colors,
      patterns,
      details,
      outlines,
      classes: [...greeningClasses(greeningLayer, ramps, label), boundariesClass(label)],
      note: label('intel.uc2.legend.greeningNote'),
    };
  }

  /**
   * Country level (2 Oct chart round, design §2.1). A country is a summary of its
   * watersheds: the only number the run rolls up is the share of them with a positive
   * pattern, so the pattern chip paints that in three steps of the positive green and
   * every other chip leaves the countries neutral and says, in the legend, that its
   * evidence is a watershed-level reading. Inventing a country colour out of a mean would
   * be a verdict the engine never reached.
   */
  if (level === 'admin0') {
    const positiveColors = familySteps(POSITIVE_BASE);
    const croplandSteps = familySteps(CROPLAND_BASE);
    const rainSteps = familySteps(RAINFALL_BASE);
    const practiceSteps = familySteps(PRACTICE_BASE);

    // Both raster chips have already returned above, and they are the only two this level
    // greys out — so every chip that reaches here has a country reading of its own.
    const family =
      chip === 'pattern'
        ? positiveColors
        : chip === 'cropland'
          ? croplandSteps
          : chip === 'rainfall'
            ? rainSteps
            : practiceSteps;
    const key =
      chip === 'pattern'
        ? 'positive'
        : chip === 'cropland'
          ? 'growing'
          : chip === 'rainfall'
            ? 'wetter'
            : 'practice';

    for (const row of rows) {
      const mine = watershedsOf(watershedRows, row.unit_id);
      const value =
        chip === 'pattern'
          ? (typeof row.share_positive_pct === 'number' ? row.share_positive_pct : null)
          : chip === 'cropland'
            ? shareOf(mine, (w) =>
                typeof w.cropland_delta_pct === 'number' ? w.cropland_delta_pct > band : null,
              )
            : chip === 'rainfall'
              ? shareOf(mine, (w) =>
                  w.rain_tendency ? w.rain_tendency === 'wetter' : null,
                )
              : meanOf(mine.map((w) => w.adoption?.[practice] ?? null));
      // Adoption is already a percentage of farmers; the other three are shares of
      // watersheds. Both read in three steps of the chip's own family.
      const step = chip === 'practices' ? (value == null ? null : practiceStep(value)) : positiveStep(value);
      if (step == null) {
        hatch(row.unit_id, row.reason ?? null);
        continue;
      }
      colors[row.unit_id] = family[step];
      details[row.unit_id] = label(`intel.uc2.legend.${key}.${POSITIVE_KEYS[step]}`);
    }
    POSITIVE_KEYS.forEach((name, i) =>
      add(label(`intel.uc2.legend.${key}.${name}`), family[i]!, (id) => colors[id] === family[i]),
    );
    add(label('intel.uc2.legend.notAssessed'), NEUTRAL_COLOR, (id) => patterns[id] === 'hatched', true);
    if (selected && !outlines[selected]) outlines[selected] = SELECTED_OUTLINE_COLOR;
    return { colors, patterns, details, outlines, classes, note };
  }

  const croplandColors = familySteps(CROPLAND_BASE);
  const rainColors = familySteps(RAINFALL_BASE);
  const practiceColors = familySteps(PRACTICE_BASE);

  for (const row of rows) {
    const id = row.unit_id;

    if (chip === 'pattern') {
      const color = UC2_CATEGORY_COLORS[row.category];
      if (!color) {
        hatch(id, row.reason ?? null);
      } else {
        colors[id] = color;
        details[id] = label(`intel.uc2.category.${row.category}`, { defaultValue: row.category });
      }
      continue;
    }

    if (chip === 'cropland') {
      const step = croplandStep(row.cropland_delta_pct, band);
      if (step == null) {
        hatch(id, row.reason ?? null);
      } else {
        colors[id] = croplandColors[step];
        details[id] = label(`intel.uc2.legend.cropland.${CROPLAND_KEYS[step]}`);
      }
      continue;
    }

    if (chip === 'rainfall') {
      const step = rainStep(row.rain_tendency);
      if (step == null) {
        hatch(id, row.reason ?? null);
      } else {
        colors[id] = rainColors[step];
        details[id] = label(`intel.uc2.legend.rain.${RAIN_KEYS[step]}`);
      }
      continue;
    }

    // `practices`, the last chip that paints a choropleth of its own.
    const pct = row.adoption?.[practice];
    if (typeof pct !== 'number') {
      hatch(id, 'no_survey_value');
    } else {
      const step = practiceStep(pct);
      colors[id] = practiceColors[step];
      details[id] = label(`intel.uc2.legend.practice.${PRACTICE_KEYS[step]}`);
    }
  }

  // The legend, in the order the map's own palette reads.
  if (chip === 'pattern') {
    for (const cat of ['positive', 'no_pattern'] as const) {
      add(
        label(`intel.uc2.category.${cat}`),
        UC2_CATEGORY_COLORS[cat]!,
        (id) => rows.find((r) => r.unit_id === id)?.category === cat,
      );
    }
  } else if (chip === 'cropland') {
    CROPLAND_KEYS.forEach((key, i) =>
      add(label(`intel.uc2.legend.cropland.${key}`), croplandColors[i]!, (id) => colors[id] === croplandColors[i]),
    );
  } else if (chip === 'rainfall') {
    RAIN_KEYS.forEach((key, i) =>
      add(label(`intel.uc2.legend.rain.${key}`), rainColors[i]!, (id) => colors[id] === rainColors[i]),
    );
  } else if (chip === 'practices') {
    PRACTICE_KEYS.forEach((key, i) =>
      add(label(`intel.uc2.legend.practice.${key}`), practiceColors[i]!, (id) => colors[id] === practiceColors[i]),
    );
  }
  // One hatched row for whatever could not be assessed on this chip, whatever the chip is.
  add(label('intel.uc2.legend.notAssessed'), NEUTRAL_COLOR, (id) => patterns[id] === 'hatched', true);

  // The clicked watershed is outlined on every chip (design §3).
  if (selected && !outlines[selected]) outlines[selected] = SELECTED_OUTLINE_COLOR;

  return { colors, patterns, details, outlines, classes, note };
}
